import {
  Copy,
  Check,
  Trash2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Pencil,
  ThumbsUp,
  ThumbsDown,
  MessageSquareReply,
  StopCircle,
  Ban,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/utils';
import FeedbackModal from './FeedbackModal';
import { MessageStatus } from '@/types';

interface MessageActionsProps {
  onCopy: (e: React.MouseEvent) => void;
  copied?: boolean;
  onRetry?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  onEditSave?: (e: React.MouseEvent) => void;
  // 当为只读模式时隐藏所有操作按钮（保留时间和版本切换器）
  readOnly?: boolean;
  isUser?: boolean;
  // 流式传输状态 - 禁用按钮但仍显示
  isStreaming?: boolean;
  // 版本切换相关
  multiVersion?: number;
  activeChoice?: number;
  onPreviousChoice?: () => void;
  onNextChoice?: () => void;
  // 反馈相关
  conversationId?: string;
  messageId?: number;
  initialScore?: number; // 初始评分状态：0=未评分，1=差评，10=好评
  onFeedbackSubmit?: (
    score: number,
    feedback: string,
    issueTypes: string
  ) => void;
  // 时间相关
  createdAt?: Date | string;
  // Client 信息
  client?: {
    id: number | string;
    name: string;
    logo?: string;
  };
  // 🔥 群聊相关
  isGroupChat?: boolean;
  onReply?: (e: React.MouseEvent) => void; // 回复按钮回调
  // 🔥 消息取消相关
  messageStatus?: MessageStatus; // 消息状态：waiting/pending/ongoing/canceled
  onCancel?: (e: React.MouseEvent) => void; // 取消消息回调
  isCanceling?: boolean; // 是否正在取消中
}
/**
 * MessageActions Component
 * 渲染消息操作按钮（复制、重试、删除）以及版本切换按钮
 */
export default function MessageActions({
  onCopy,
  copied = false,
  onRetry,
  onDelete,
  onEditSave,
  multiVersion,
  activeChoice = 0,
  onPreviousChoice,
  onNextChoice,
  isUser,
  readOnly = false,
  isStreaming = false,
  conversationId: _conversationId,
  messageId,
  initialScore = 0,
  onFeedbackSubmit,
  createdAt,
  client,
  isGroupChat = false,
  onReply,
  messageStatus,
  onCancel,
  isCanceling = false,
}: MessageActionsProps) {
  const { t } = useTranslation();
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative'>(
    'positive'
  );
  // 根据 initialScore 初始化按钮高亮状态
  const [thumbsUpActive, setThumbsUpActive] = useState(initialScore === 10);
  const [thumbsDownActive, setThumbsDownActive] = useState(initialScore === 1);

  // 检查是否有有效的后端消息ID
  const hasValidBackendId = typeof messageId === 'number' && messageId > 0;

  const handleThumbsUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFeedbackType('positive');
    setFeedbackModalOpen(true);
  };

  const handleThumbsDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFeedbackType('negative');
    setFeedbackModalOpen(true);
  };

  const handleFeedbackSubmit = (feedback: string, issueTypes: string) => {
    const score = feedbackType === 'positive' ? 10 : 1;

    if (onFeedbackSubmit) {
      onFeedbackSubmit(score, feedback, issueTypes);
    }

    // Update highlight state
    if (feedbackType === 'positive') {
      setThumbsUpActive(true);
      setThumbsDownActive(false);
    } else {
      setThumbsUpActive(false);
      setThumbsDownActive(true);
    }
  };

  return (
    <div className="flex flex-wrap items-start gap-1">
      {/* Client 信息和时间显示 - 左边，仅在 assistant 消息显示 */}
      {!isUser && (client || createdAt) && (
        <div className="flex items-center gap-1.5 text-sm opacity-65 whitespace-nowrap mr-2">
          {client && (
            <div className="flex items-center gap-1">
              {client.logo ? (
                <img
                  src={client.logo}
                  alt={client.name}
                  className="w-4 h-4 rounded-sm object-cover"
                />
              ) : null}
              <span>{client.name}</span>
              {createdAt && <span className="opacity-50">·</span>}
            </div>
          )}
          {createdAt && <span>{formatDateTime(createdAt)}</span>}
        </div>
      )}
      {/* 版本切换器 - 仅在有多个版本时显示，群聊模式下隐藏 */}
      {multiVersion && multiVersion > 1 && !isGroupChat && (
        <div
          className={`flex items-center gap-1 mr-2 px-1 text-sm bg-white/60 backdrop-blur-sm rounded border shadow-sm transition-all ${
            isStreaming
              ? 'text-gray-400 border-gray-200 opacity-50'
              : 'text-gray-600 border-gray-300'
          }`}
        >
          <button
            onClick={onPreviousChoice}
            disabled={isStreaming || activeChoice === 0}
            className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title={
              isStreaming
                ? t('chat.waitForResponse')
                : t('messageActions.previousVersion')
            }
          >
            <ChevronLeft size={14} />
          </button>
          <span className="px-1 font-medium">
            {activeChoice + 1}/{multiVersion}
          </span>
          <button
            onClick={onNextChoice}
            disabled={isStreaming || activeChoice === multiVersion - 1}
            className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title={
              isStreaming
                ? t('chat.waitForResponse')
                : t('messageActions.nextVersion')
            }
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
      {/* 🔥 Copy 按钮 - 始终显示（viewer 可用） */}
      <button
        onClick={onCopy}
        className={`rounded-sm inline-flex items-center gap-1 px-1 py-1 text-sm transition-all shadow-sm border ${
          copied
            ? 'text-green-700 bg-green-100 border-green-300 hover:bg-green-200'
            : 'text-gray-700 hover:bg-gray-200 backdrop-blur-sm border-transparent'
        }`}
        title={copied ? t('common.copied') : t('chat.copyMessage')}
      >
        {copied ? (
          <>
            <Check size={12} />
          </>
        ) : (
          <>
            <Copy size={12} />
          </>
        )}
      </button>

      {/* 🔥 Thumbs Up/Down 按钮 - 始终显示（viewer 可用） */}
      {/* Thumbs Up Button - 只在有有效后端ID时显示，群聊模式下隐藏 */}
      {!isUser && hasValidBackendId && !isGroupChat && (
        <button
          onClick={handleThumbsUp}
          className={`rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm ${
            thumbsUpActive
              ? 'text-blue-700 bg-blue-100 hover:bg-blue-200'
              : 'text-gray-700 hover:bg-gray-200 backdrop-blur-sm'
          }`}
          title={t('messageActions.likeResponse')}
        >
          <ThumbsUp size={12} />
        </button>
      )}

      {/* Thumbs Down Button - 只在有有效后端ID时显示，群聊模式下隐藏 */}
      {!isUser && hasValidBackendId && !isGroupChat && (
        <button
          onClick={handleThumbsDown}
          className={`rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm ${
            thumbsDownActive
              ? 'text-red-700 bg-red-100 hover:bg-red-200'
              : 'text-gray-700 hover:bg-gray-200 backdrop-blur-sm'
          }`}
          title={t('messageActions.dislikeResponse')}
        >
          <ThumbsDown size={12} />
        </button>
      )}

      {/* 操作按钮 - 只在非只读模式时显示（Retry/Edit/Delete/Cancel） */}
      {!readOnly && (
        <>
          {/* 🔥 群聊：用户消息显示回复按钮（群聊模式下不禁用） */}
          {isGroupChat && isUser && onReply && (
            <button
              onClick={onReply}
              className="rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm text-gray-700 hover:bg-gray-200 backdrop-blur-sm"
              title={t('chat.reply')}
            >
              <MessageSquareReply size={14} />
            </button>
          )}

          {/* Retry 按钮 - 群聊模式下隐藏 */}
          {onRetry && !isGroupChat && (
            <button
              onClick={onRetry}
              disabled={isStreaming}
              className={`rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm ${
                isStreaming
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200 backdrop-blur-sm'
              }`}
              title={
                isStreaming
                  ? t('chat.waitForResponse')
                  : t('messageActions.retry')
              }
            >
              <RotateCcw size={12} />
            </button>
          )}
          {/* Edit 按钮 - 群聊模式下隐藏 */}
          {isUser && !isGroupChat ? (
            <button
              onClick={onEditSave}
              disabled={isStreaming}
              className={`rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm ${
                isStreaming
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200 backdrop-blur-sm'
              }`}
              title={
                isStreaming ? t('chat.waitForResponse') : t('chat.editMessage')
              }
            >
              <Pencil
                size={12}
                className={isStreaming ? 'text-gray-400' : 'text-gray-700'}
              />
            </button>
          ) : !isUser && !isGroupChat ? (
            <button
              onClick={onDelete}
              disabled={isStreaming}
              className={`rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm ${
                isStreaming
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200 backdrop-blur-sm'
              }`}
              title={
                isStreaming
                  ? t('chat.waitForResponse')
                  : t('chat.deleteMessage')
              }
            >
              <Trash2 size={12} />
            </button>
          ) : null}

          {/* 🔥 Cancel 按钮 - 仅在消息状态为 waiting/pending/ongoing/canceled 时显示 */}
          {messageStatus && (
            <button
              onClick={onCancel}
              disabled={messageStatus === 'canceled' || isCanceling}
              className={`rounded-sm inline-flex items-center gap-1 px-2 py-1 text-sm transition-all shadow-sm ${
                messageStatus === 'canceled'
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                  : isCanceling
                    ? 'text-amber-600 cursor-wait'
                    : 'text-red-600 hover:bg-red-100 backdrop-blur-sm'
              }`}
              title={
                messageStatus === 'canceled'
                  ? t('messageActions.messageCanceled')
                  : isCanceling
                    ? t('messageActions.canceling')
                    : t('messageActions.cancelMessage')
              }
            >
              {messageStatus === 'canceled' ? (
                <>
                  <Ban size={12} />
                  <span>{t('common.canceled')}</span>
                </>
              ) : isCanceling ? (
                <>
                  <StopCircle size={12} className="animate-pulse" />
                  <span>{t('common.canceling')}</span>
                </>
              ) : (
                <>
                  <StopCircle size={12} />
                  <span>{t('common.cancel')}</span>
                </>
              )}
            </button>
          )}
        </>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={feedbackType}
      />
    </div>
  );
}
